// package imports
import React, { useEffect, useState } from "react";
import { View, Text, Pressable, HStack, useToast } from "native-base";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
// components
import ContentContainer from "atoms/ContentContainer";
import Breadcrumbs from "atoms/Breadcrumbs";
import SummaryBox from "atoms/SummaryBox";
import NoData from "atoms/NoData";
import CustomTable from "molecules/CustomTable";
import PaginationMui from "molecules/Pagination";
import SearchBar from "atoms/SearchBar";
// utilities
import { fetchSdRequestDetails } from "api/apiCall";
import { getStore } from "store/storeUtils";
import { SD_LANDING_PAGE } from "global/paths";
import { AiOutlineDownload } from "react-icons/ai";
import colors from "theme/colors";
import CreateModal from "molecules/CreateModal";
import { CREATE_MODALS } from "global/constants";
import { showErrorToast } from "utils/utilities";

const SDDetails = ({ isMinimize, userEmail = "" }) => {
  const toast = useToast();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { modal, setModal, resetLoginData } = getStore();
  const location = useLocation();
  const { data = "" } = location.state || {};

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [totalPageNumber, setTotalPageNumber] = useState(0);

  const {
    isLoading,
    mutate: getSdRequestDetails,
    isSuccess: isSdRequestDetailsSuccess,
    data: sdRequestDetailsData,
    isError: isSdRequestDetailsError,
    error: sdRequestDetailsError,
  } = useMutation({
    mutationFn: (payload) => fetchSdRequestDetails({ requestId: data, payload }),
  });

  useEffect(() => {
    const skipElements = currentPage == 1 ? 0 : 50 * (currentPage - 1);
    const search = searchText ? { search: searchText } : {};
    getSdRequestDetails({
      limit: 50,
      skip: skipElements,
      status: "REQUESTED",
      ...search,
    });
  }, [currentPage]);

  useEffect(() => {
    if (isSdRequestDetailsError) {
      showErrorToast({
        error: sdRequestDetailsError,
        toast,
        navigate,
        resetLoginData,
      });
    }
  }, [isSdRequestDetailsError, sdRequestDetailsError]);

  useEffect(() => {
    if (isSdRequestDetailsSuccess) {
      setTotalPageNumber(Math.ceil((sdRequestDetailsData?.count || 0) / 50));
      setCurrentData(sdRequestDetailsData?.data?.records);
    }
  }, [isSdRequestDetailsSuccess, sdRequestDetailsData]);

  const onSearch = (searchText) => {
    setSearchText(searchText);
    setCurrentPage(1);
    getSdRequestDetails({
      search: searchText,
      limit: 50,
      skip: 0,
      status: "REQUESTED",
    });
  };

  const onDownloadPress = () => {
    setModal({
      ...modal,
      type: CREATE_MODALS.exportSDDataDitolak,
      data: {
        email: userEmail,
        requestId: data,
        search: searchText,
        status: "success",
      },
    });
  };

  return (
    <>
      <ContentContainer title={t("salaryDisbursement.SDDetailsBox.title")} flex={[1, 1, 1, 0.85, 0.85]}>
        <Breadcrumbs
          currentPage={`${t("salaryDisbursement.SDDetailsBox.breadcrumb")} ${data}`}
          parentScreen={[{ name: "sideBar.salaryDisbursement", link: SD_LANDING_PAGE }]}
          style={{ marginTop: "10px", marginHorizontal: "0px" }}
        />

        <SummaryBox title={t("salaryDisbursement.list.title")} maxWidth="1600px">
          <HStack p="4">
            <SearchBar placeholder={t("salaryDisbursement.SDDetailsBox.placeholder")} onSearch={onSearch} />
            <View w="180px" ml="24px">
              <Pressable onPress={onDownloadPress} isDisabled={data === ""}>
                <HStack
                  justifyContent="center"
                  space="2"
                  borderWidth={1}
                  borderColor={colors.disabled.smoke}
                  borderRadius={4}
                  padding={2}
                >
                  <Text fontSize="xs">{t("salaryDisbursement.list.header.download")}</Text>
                  <AiOutlineDownload size="17px" />
                </HStack>
              </Pressable>
            </View>
          </HStack>

          {!currentData ? (
            <NoData />
          ) : (
            <>
              <CustomTable
                isMinimize={isMinimize}
                data={currentData}
                isLoading={isLoading}
                skip={currentPage == 1 ? 0 : 50 * (currentPage - 1)}
                headers={[
                  "salaryDisbursement.list.header.no",
                  "salaryDisbursement.list.header.employeeName",
                  "salaryDisbursement.list.header.mobilePhoneNumber",
                  "salaryDisbursement.list.header.BankName",
                  "salaryDisbursement.list.header.accountNumber",
                  "salaryDisbursement.list.header.nameOfAccountOwner",
                  "salaryDisbursement.list.header.totalNominal",
                  "salaryDisbursement.list.header.disbursementStatus",
                  "salaryDisbursement.list.header.notes",
                ]}
                fields={[
                  "no",
                  "employeeName",
                  "phoneNumber",
                  "bankName",
                  "accountNumber",
                  "beneficiaryName",
                  "amount",
                  "disbursementStatus",
                  "transactionNote",
                ]}
              />
              <PaginationMui
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPageNumber={totalPageNumber}
              />
            </>
          )}
        </SummaryBox>
      </ContentContainer>
      <CreateModal />
    </>
  );
};

export default SDDetails;
